import { useState, PropsWithChildren, useEffect } from 'react';
import { Form, Field, useForm } from 'react-final-form';

import { ProductTag } from '@axis/graphql';
import { useIsTabletOrMobile } from '@axis/hooks/mobile';
import { useAnimation } from '@axis/components/AnimationProvider';

export type SortByEnum = 'MENU_ORDER' | 'LOW_TO_HIGH' | 'HIGH_TO_LOW';
export interface SortAndFilterState {
  sortBy: SortByEnum;
  tags: string[];
}

export interface FiltersFormValues {
  sortBy: SortByEnum;
  [slug: string]: boolean | SortByEnum;
}

type FilterFormButtonsProps = {
  updateFilters: (state: SortAndFilterState) => void;
}

function FilterFormButtons({ updateFilters }: FilterFormButtonsProps) {
  const { reset } = useForm();

  const clearFilters = () => {
    reset();
    updateFilters({
      sortBy: 'MENU_ORDER',
      tags: [],
    });
  };
  return (
    <div className="drawer-footer">
      <button
        type="button"
        className="secondary-button w-1/2"
        onClick={clearFilters}
      >
        Clear
      </button>
      <button
        type="submit"
        className="primary-button w-1/2 "
      >
        Search
      </button>
    </div>
  );
}

export interface WrapperProps {
  className?: string;
  onSubmit: (values: FiltersFormValues) => void;
  initialValues: {
    sortBy: SortByEnum;
    [key: string]: boolean|SortByEnum;
  };
}

function MobileWrapper(props: PropsWithChildren<WrapperProps>) {
  const {
    children,
    onSubmit,
    initialValues,
    className,
  } = props;
  const [showingModal, toggleModal] = useState(false);

  const { anime } = useAnimation();

  useEffect(() => {
    anime(
      {
        targets: '.filters-button',
        translateY: [200, 0],
        opacity: [0, 1],
        duration: 300,
        delay: 500,
        easing: 'easeInOutSine',
      },
    );
  }, []);

  const submitAndClose = async (values: FiltersFormValues) => {
    await onSubmit(values);
    toggleModal(false);
  };

  useEffect(() => {
    anime(
      {
        targets: '.filters-drawer',
        translateY: [300, 0],
        opacity: [0, 1],
        duration: 300,
        delay: 100,
        easing: 'easeInOutSine',
      },
    );
    anime(
      {
        targets: '.filters-drawer-overlay',
        opacity: [0, 0.8],
        duration: 500,
        easing: 'easeInOutSine',
      },
    );
  }, [showingModal]);

  const closeModal = () => {
    anime(
      {
        targets: '.filters-drawer',
        translateY: [0, 300],
        opacity: [1, 0],
        duration: 300,
        easing: 'easeInOutSine',
        complete: () => toggleModal(false),
      },
    );
    anime(
      {
        targets: '.filters-drawer-overlay',
        opacity: [0.8, 0],
        duration: 300,
        easing: 'easeInOutSine',
      },
    );
  };

  return (
    <>
      <button
        type="button"
        className="filters-button icon-button primary-button w-2/5 fixed inset-x-0 mx-auto bottom-10 z-30"
        onClick={() => toggleModal(true)}
      >
        <i className="fa-solid fa-filter mr-1" />
        Sort & Filter
      </button>
      {showingModal && (
        <Form onSubmit={submitAndClose} initialValues={initialValues}>
          {({ handleSubmit }) => (
            <form className={className} onSubmit={handleSubmit}>
              <div
                className="filters-drawer-overlay drawer-overlay"
                onClick={() => toggleModal(false)}
                onKeyPress={() => toggleModal(false)}
                role="button"
                aria-label="overlay"
                tabIndex={-1}
              />
              <div
                className="filters-drawer drawer left-0 bottom-0 w-full"
              >
                <div className="drawer-content">
                  <button
                    type="button"
                    className="drawer-close-button"
                    onClick={closeModal}
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                  <div className="drawer-header">
                    <i className="fa-solid fa-filter mr-1" />
                    Filters
                  </div>
                  {children}
                </div>
              </div>
            </form>
          )}
        </Form>
      )}
    </>
  );
}

function DesktopWrapper(props: PropsWithChildren<WrapperProps>) {
  const {
    children,
    onSubmit,
    initialValues,
    className,
  } = props;
  return (
    <div className={`${className} shop-page-section w-2/6 p-0 md:pb-0`.trim()}>
      <span className="shop-page-section-header bg-transparent">
        <i className="fa-solid fa-filter mr-1" />
        Filters
      </span>
      <Form onSubmit={onSubmit} initialValues={initialValues}>
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit} className="w-full h-full flex flex-col">
            {children}
          </form>
        )}
      </Form>
    </div>
  );
}

export interface ProductGridFiltersProps {
  allTags: ProductTag[];
  filters: SortAndFilterState;
  updateFilters: (state: SortAndFilterState) => void;
}

export function ProductCardGridFilters(props: ProductGridFiltersProps) {
  const { allTags, updateFilters, filters } = props;
  const isTabletOrMobile = useIsTabletOrMobile();
  const Wrapper = isTabletOrMobile ? MobileWrapper : DesktopWrapper;
  const { newTimeline, add, anime } = useAnimation();

  useEffect(() => {
    newTimeline(
      'product-card-grid-filters',
      {
        direction: 'normal',
        autoplay: true,
        easing: 'easeInOutSine',
      },
    );
    [...Array(4)].forEach((_, index) => {
      add(
        'product-card-grid-filters',
        {
          targets: `.product-card-grid-filters .radio-button-wrapper:nth-of-type(${Number(index) + 1})`,
          opacity: [0, 1],
          duration: 150,
          delay: anime.stagger(50),
        },
      );
    });
    [...Array(allTags.length + 1)].forEach((_, index) => {
      add(
        'product-card-grid-filters',
        {
          targets: `.product-card-grid-filters .checkbox-wrapper:nth-of-type(${Number(index) + 1})`,
          opacity: [0, 1],
          duration: 150,
          delay: anime.stagger(50, { start: 150 }),
        },
      );
    });
    add(
      'product-card-grid-filters',
      {
        targets: '.product-card-grid-filters .drawer-footer',
        translateY: [200, 0],
        duration: 300,
        delay: 150,
      },
    );
  }, []);

  const onSubmit = async (values: FiltersFormValues) => {
    const { sortBy, ...tags } = values;

    updateFilters({
      sortBy,
      tags: Object.entries(tags)
        .filter(([, isTrue]) => isTrue)
        .map(([tag]) => tag),
    });
  };

  const initialValues = {
    sortBy: filters.sortBy,
    ...filters.tags.reduce(
      (results, nextTag): { [key: string]: boolean } => ({
        ...results,
        [nextTag]: true,
      }),
      {},
    ),
  };

  return (
    <Wrapper
      className="product-card-grid-filters"
      onSubmit={onSubmit}
      initialValues={initialValues}
    >
      <div>
        <div className="drawer-subheader md:rounded-t-xl">
          Sort By
        </div>
        <div className="radio-button-wrapper mr-4 mb-4 px-5">
          <label className="radio-button-label">
            <Field component="input" type="radio" name="sortBy" value="LOW_TO_HIGH" className="hidden" />
            <span className="radio-button" />
            Low to High
          </label>
        </div>

        <div className="radio-button-wrapper mr-4 mb-4 px-5">
          <label className="radio-button-label">
            <Field component="input" type="radio" name="sortBy" value="HIGH_TO_LOW" className="hidden" />
            <span className="radio-button" />
            High to Low
          </label>
        </div>

        <div className="radio-button-wrapper mr-4 px-5">
          <label className="radio-button-label">
            <Field component="input" type="radio" name="sortBy" value="MENU_ORDER" className="hidden" />
            <span className="radio-button" />
            Most Popular
          </label>
        </div>

      </div>
      <div>
        <div className="drawer-subheader mt-2">Tags</div>
        {allTags.map(({ id, name, slug }) => (
          <div key={id} className="checkbox-wrapper mb-4 px-5">
            <Field component="input" type="checkbox" name={slug as string} id={slug} aria-describedby={slug} className="checkbox" />
            <label htmlFor={slug as string} className="checkbox-label">{name}</label>
          </div>
        ))}
      </div>
      <FilterFormButtons updateFilters={updateFilters} />
    </Wrapper>
  );
}
